/**
 * The workspace's Mail and Calendar view models (one set per signed-in workspace, made by the app shell), found by
 * the Mail / Contacts / Calendar screens, Settings → Mail and the sidebar's unread badge — the same registry pattern
 * as Later. Also the composer's open/close store (one composer at a time) and a fan-out of the workspace's mail
 * events for per-screen thread view models.
 */
import type { PersistentEvent } from "@bundle/contracts";
import type { BookingPagesViewModel, CalendarViewModel, MeetSwapViewModel, ComposerStart, ContactsViewModel, MailNotifyViewModel, MailboxViewModel, MailSettingsViewModel, PickedFile, WorkspaceViewModel } from "@bundle/core";
import { useSyncExternalStore } from "react";
import { useWorkspace } from "../hooks/index.ts";

export type MailServices = {
  mailbox: MailboxViewModel;
  contacts: ContactsViewModel;
  calendar: CalendarViewModel;
  /** Replacing Google Meet with a Bundle meeting: the event page's prompt, its run and Settings → Calendar. */
  meetSwap: MeetSwapViewModel;
  mailSettings: MailSettingsViewModel;
  /** My new-mail notification preferences (Settings → Notifications). */
  notify: MailNotifyViewModel;
  /**
   * Opens an attachment ("open": the desktop's default app, a browser tab for what one shows, else a download) or only
   * saves it ("download"). The platform fetches it with my credentials; `onProgress` is 0…1 while it comes. Call it from
   * the click: a browser tab has to open then. `savedPath`: where the desktop app put it.
   */
  openAttachment: (
    messageId: string,
    attachment: { id: string; filename: string; contentType?: string; size?: number },
    intent: "open" | "download",
    onProgress?: (fraction: number) => void,
  ) => Promise<{ savedPath?: string }>;
  /** Desktop: shows a saved attachment in Finder / Explorer. */
  revealAttachment?: (path: string) => void;
  /** Uploads a file to attach to an email → its file id (`SendMailRequest.fileIds`). */
  uploadAttachment: (file: PickedFile) => Promise<string>;
};

const byWorkspace = new Map<WorkspaceViewModel, MailServices>();
const listeners = new Set<() => void>();
const changed = () => {
  for (const l of Array.from(listeners)) l();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** The app shell: makes `mail` the workspace's (until the returned function runs). */
export function registerMail(ws: WorkspaceViewModel, mail: MailServices): () => void {
  byWorkspace.set(ws, mail);
  changed();
  return () => {
    if (byWorkspace.get(ws) === mail) byWorkspace.delete(ws);
    changed();
  };
}

/** This workspace's mail services (undefined outside a workspace shell). */
export function useMail(): MailServices | undefined {
  const ws = useWorkspace();
  return useSyncExternalStore(subscribe, () => byWorkspace.get(ws.vm));
}

// ---------- Calendar (its own area; no Mail needed) ----------

/** The Calendar area's view models: events and the meet-swap prompt (the same instances as `MailServices`) and my booking pages. */
export type CalendarServices = { calendar: CalendarViewModel; booking: BookingPagesViewModel; meetSwap: MeetSwapViewModel };

const calendars = new Map<WorkspaceViewModel, CalendarServices>();

/** The app shell: makes `services` the workspace's Calendar (until the returned function runs). */
export function registerCalendar(ws: WorkspaceViewModel, services: CalendarServices): () => void {
  calendars.set(ws, services);
  changed();
  return () => {
    if (calendars.get(ws) === services) calendars.delete(ws);
    changed();
  };
}

/** This workspace's Calendar services (undefined outside a workspace shell). */
export function useCalendar(): CalendarServices | undefined {
  const ws = useWorkspace();
  return useSyncExternalStore(subscribe, () => calendars.get(ws.vm));
}

// ---------- Mail events for open threads ----------

const eventListeners = new Set<(event: PersistentEvent) => void>();

export function onMailEvent(listener: (event: PersistentEvent) => void): () => void {
  eventListeners.add(listener);
  return () => {
    eventListeners.delete(listener);
  };
}

/** The app shell: a `mail.*` / `calendar.*` event arrived. */
export function emitMailEvent(event: PersistentEvent): void {
  for (const l of Array.from(eventListeners)) l(event);
}

// ---------- The composer ----------

let composer: { start: ComposerStart; seq: number } | undefined;
let seq = 0;

export function openComposer(start: ComposerStart): void {
  composer = { start, seq: ++seq };
  changed();
}

export function closeComposer(): void {
  composer = undefined;
  changed();
}

export function useComposer(): { start: ComposerStart; seq: number } | undefined {
  return useSyncExternalStore(subscribe, () => composer);
}

// ---------- Undo (Gmail's "Conversation archived · Undo") ----------

let undo: { message: string; run: () => void; seq: number } | undefined;

/** Shows the undo toast for an action that moved conversations (the shell's MailComposerHost renders it). */
export function showMailUndo(message: string, run: () => void): void {
  undo = { message, run, seq: ++seq };
  changed();
}

export function clearMailUndo(): void {
  undo = undefined;
  changed();
}

export function useMailUndo(): { message: string; run: () => void; seq: number } | undefined {
  return useSyncExternalStore(subscribe, () => undo);
}
